import { FC, useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { Typography, FormControlLabel, Checkbox, TextField, withStyles, IconButton, Chip } from '@material-ui/core';
import { DeleteOutline, ExpandMore, ChevronRight } from '@material-ui/icons';
import SpeculationRule from '../../../../entity/SpeculationRule';
import { Organization } from '@kaizenplatform/intelligence-entity';
import { PaperButton } from '../../primitive/Page';
import { PaperContent, PaperDivider, PaperItem, StyledPaper } from '../../primitive/Paper';
import StyledLoading from './StyledLoading';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';

type Props = {
  organization: Organization | null;
  speculationRule: SpeculationRule | null;
  speculationRuleLoading: boolean;
  getSpeculationRule: () => void;
  putSpeculationRule: (speculationRule: SpeculationRule) => void;
  onMount: () => void;
};

type PathGrouping = {
  id: string;
  name: string;
  paths: Array<{ id: string; value: string }>;
};

const LinkPrefetchEditTab: FC<Props> = ({
  organization,
  speculationRule,
  speculationRuleLoading,
  getSpeculationRule,
  putSpeculationRule,
  onMount,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [pageUrlRegexp, setPageUrlRegexp] = useState('');
  const [regexError, setRegexError] = useState(false);
  const [pageUrlRegexpError, setPageUrlRegexpError] = useState(false);
  const [whitelistGroups, setWhitelistGroups] = useState<PathGrouping[]>([]);
  const [blacklist, setBlacklist] = useState<Array<{ id: string; value: string }>>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [blacklistExpanded, setBlacklistExpanded] = useState(true);
  const [groupNameErrors, setGroupNameErrors] = useState<Set<string>>(new Set());
  const groupNameRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const groupPathRefs = useRef<{ [key: string]: { [pathId: string]: HTMLInputElement | null } }>({});
  const blacklistRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    if (organization) {
      onMount();
    }
  }, [organization]);

  useEffect(() => {
    if (speculationRule) {
      setEnabled(!!speculationRule.enabled);
      setPageUrlRegexp(speculationRule.pageUrlRegexp ?? '');
      const groups = (speculationRule.whitelistPathGrouping ?? []).map(([groupName, paths]) => ({
        id: Math.random().toString(36).substr(2, 9),
        name: groupName,
        paths: paths.map((path) => ({
          id: Math.random().toString(36).substr(2, 9),
          value: path,
        })),
      }));
      setWhitelistGroups(groups);
      // 初期表示時に全グループを展開
      setExpandedGroups(new Set(groups.map((g) => g.id)));
      setBlacklist(
        (speculationRule.blacklistPathPatterns ?? []).map((pattern) => ({
          id: Math.random().toString(36).substr(2, 9),
          value: pattern,
        })),
      );
    }
  }, [speculationRule]);

  const removeGroup = (groupId: string) => {
    setWhitelistGroups(whitelistGroups.filter((group) => group.id !== groupId));
  };

  const addPathToGroup = (groupId: string) => {
    setWhitelistGroups(
      whitelistGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              paths: [...group.paths, { id: Math.random().toString(36).substr(2, 9), value: '' }],
            }
          : group,
      ),
    );
  };

  const removePathFromGroup = (groupId: string, pathId: string) => {
    setWhitelistGroups(
      whitelistGroups.map((group) =>
        group.id === groupId ? { ...group, paths: group.paths.filter((path) => path.id !== pathId) } : group,
      ),
    );
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const removeBlacklist = (id: string) => {
    setBlacklist(blacklist.filter((item) => item.id !== id));
  };

  const handleSave = () => {
    let hasError = false;

    // 対象ページURLのバリデーション
    if (!pageUrlRegexp.trim()) {
      setPageUrlRegexpError(true);
      hasError = true;
    } else {
      setPageUrlRegexpError(false);
    }

    // グループ名のバリデーション
    const errorGroupIds = new Set<string>();
    whitelistGroups.forEach((group) => {
      const groupName = groupNameRefs.current[group.id]?.value?.trim() || '';
      if (!groupName) {
        errorGroupIds.add(group.id);
      }
    });

    if (errorGroupIds.size > 0) {
      setGroupNameErrors(errorGroupIds);
      // エラーのあるグループを展開
      setExpandedGroups((prev) => new Set([...prev, ...errorGroupIds]));
      hasError = true;
    } else {
      setGroupNameErrors(new Set());
    }

    if (hasError) {
      return;
    }

    const currentWhitelistGrouping: [string, string[]][] = whitelistGroups.map((group) => {
      const groupName = groupNameRefs.current[group.id]?.value?.trim() || '';
      const paths = group.paths.map((path) => groupPathRefs.current[group.id]?.[path.id]?.value || '').filter((v) => v);
      return [groupName, paths] as [string, string[]];
    });

    const currentBlacklist = blacklist.map((item) => blacklistRefs.current[item.id]?.value || '').filter((v) => v);

    putSpeculationRule(
      new SpeculationRule({
        enabled,
        pageUrlRegexp,
        whitelistPathGrouping: currentWhitelistGrouping,
        blacklistPathPatterns: currentBlacklist,
        dpState: speculationRule?.dpState || 'init',
        dpActivatedAt: speculationRule?.dpActivatedAt || null,
      }),
    );
  };

  const handleCancel = () => {
    const confirm = window.confirm('編集内容を破棄して元に戻しますか？');
    if (confirm) {
      getSpeculationRule();
    }
  };

  const validateRegex = (pattern: string) => {
    if (!pattern) return true;
    try {
      new RegExp(pattern);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    setRegexError(!validateRegex(pageUrlRegexp));
  }, [pageUrlRegexp]);

  if (speculationRuleLoading) {
    return <StyledLoading />;
  }

  return (
    <StyledPaper>
      <PaperContent>
        <StyledPaperItem>
          <FormControlLabelMui
            control={<Checkbox checked={enabled} onChange={() => setEnabled(!enabled)} />}
            label="リンク先読み機能を有効にする"
          />
          <DescriptionText>
            効果の比較計測のため、条件を満たす場合でも10%の確率で機能を無効化して測定します。
          </DescriptionText>
        </StyledPaperItem>
        <StyledPaperItem>
          <SectionLabel>対象ページURL（正規表現）</SectionLabel>
          <StyledInputField
            type="text"
            value={pageUrlRegexp}
            onChange={(e) => {
              setPageUrlRegexp(e.target.value);
              // 入力時にエラーをクリア
              if (pageUrlRegexpError) {
                setPageUrlRegexpError(false);
              }
            }}
            placeholder="例: ^https://www\.example\.com/products/.*"
            error={regexError || pageUrlRegexpError}
          />
          {pageUrlRegexpError && <InputError>対象ページURLを入力してください</InputError>}
          {regexError && !pageUrlRegexpError && <InputError>有効な正規表現を入力してください</InputError>}
          <DescriptionText>この機能を有効にするページのURLの正規表現を指定してください。</DescriptionText>
        </StyledPaperItem>
        <PaperDivider />
        <StyledPaperItem>
          <SectionLabel>先読みを有効にするリンクパターン（ホワイトリスト）</SectionLabel>
          <DescriptionText>
            先読みを有効にするリンクをグループごとに管理できます。各グループに名前をつけて、複数のパターンを設定してください。<br/>
            ※グループ内の複数のパターンに一致すると重複してグループのPVに集計されますので、パターンが重複しないよう設定してください。<br/>
            ※別のグループ間でどちらにも一致するパターンがある場合には、各グループ毎にカウントされます。
          </DescriptionText>
          <GroupList>
            {whitelistGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.id);
              const hasError = groupNameErrors.has(group.id);
              return (
                <GroupCard key={group.id}>
                  <GroupCardHeader>
                    <GroupHeaderLeft>
                      <IconButton size="small" onClick={() => toggleGroup(group.id)} style={{ padding: 4 }}>
                        {isExpanded ? <ExpandMore /> : <ChevronRight />}
                      </IconButton>
                      <GroupNameInput
                        defaultValue={group.name}
                        placeholder="グループ名を入力"
                        ref={(el) => (groupNameRefs.current[group.id] = el)}
                        hasError={hasError}
                        onChange={() => {
                          // 入力時にエラーをクリア
                          if (hasError) {
                            setGroupNameErrors((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(group.id);
                              return newSet;
                            });
                          }
                        }}
                      />
                    </GroupHeaderLeft>
                    <GroupHeaderRight>
                      <PatternCountChip label={`${group.paths.length} パターン`} size="small" />
                      <IconButton size="small" onClick={() => removeGroup(group.id)} style={{ padding: 4 }}>
                        <DeleteOutline fontSize="small" style={{ color: '#999' }} />
                      </IconButton>
                    </GroupHeaderRight>
                  </GroupCardHeader>
                  {isExpanded && (
                    <PathListContainer>
                      {group.paths.map((path, index) => {
                        if (!groupPathRefs.current[group.id]) {
                          groupPathRefs.current[group.id] = {};
                        }
                        return (
                          <PathItemRow key={path.id}>
                            <PathNumber>{index + 1}.</PathNumber>
                            <PathInputField
                              defaultValue={path.value}
                              placeholder="/example/*"
                              ref={(el) => (groupPathRefs.current[group.id][path.id] = el)}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removePathFromGroup(group.id, path.id)}
                              style={{ padding: 4 }}
                            >
                              <DeleteOutline fontSize="small" style={{ color: '#999' }} />
                            </IconButton>
                          </PathItemRow>
                        );
                      })}
                      <AddPatternLink onClick={() => addPathToGroup(group.id)}>+ パターンを追加</AddPatternLink>
                    </PathListContainer>
                  )}
                </GroupCard>
              );
            })}
            <AddGroupButton
              onClick={() => {
                const newGroupId = Math.random().toString(36).substr(2, 9);
                setWhitelistGroups([
                  ...whitelistGroups,
                  {
                    id: newGroupId,
                    name: '',
                    paths: [{ id: Math.random().toString(36).substr(2, 9), value: '' }],
                  },
                ]);
                // 新しいグループをデフォルトで展開
                setExpandedGroups((prev) => new Set(prev).add(newGroupId));
              }}
            >
              + リンクグループを追加
            </AddGroupButton>
          </GroupList>
        </StyledPaperItem>
        <PaperDivider />
        <StyledPaperItem>
          <SectionLabel>先読みを禁止するリンクパターン（ブラックリスト）</SectionLabel>
          <DescriptionText>先読みを禁止するリンクのパターンをワイルドカードで指定してください。</DescriptionText>
          <GroupList>
            <GroupCard>
              <GroupCardHeader>
                <GroupHeaderLeft>
                  <IconButton
                    size="small"
                    onClick={() => setBlacklistExpanded(!blacklistExpanded)}
                    style={{ padding: 4 }}
                  >
                    {blacklistExpanded ? <ExpandMore /> : <ChevronRight />}
                  </IconButton>
                  <BlacklistLabel>ブラックリスト</BlacklistLabel>
                </GroupHeaderLeft>
                <GroupHeaderRight>
                  <PatternCountChip label={`${blacklist.length} パターン`} size="small" />
                </GroupHeaderRight>
              </GroupCardHeader>
              {blacklistExpanded && (
                <PathListContainer>
                  {blacklist.map((item, index) => (
                    <PathItemRow key={item.id}>
                      <PathNumber>{index + 1}.</PathNumber>
                      <PathInputField
                        defaultValue={item.value}
                        placeholder="/example/*"
                        ref={(el) => (blacklistRefs.current[item.id] = el)}
                      />
                      <IconButton size="small" onClick={() => removeBlacklist(item.id)} style={{ padding: 4 }}>
                        <DeleteOutline fontSize="small" style={{ color: '#999' }} />
                      </IconButton>
                    </PathItemRow>
                  ))}
                  <AddPatternLink
                    onClick={() =>
                      setBlacklist([...blacklist, { id: Math.random().toString(36).substr(2, 9), value: '' }])
                    }
                  >
                    + パターンを追加
                  </AddPatternLink>
                </PathListContainer>
              )}
            </GroupCard>
          </GroupList>
        </StyledPaperItem>
        <PaperDivider />
        <ButtonArea>
          <CancelButton onClick={handleCancel}>編集内容を元に戻す</CancelButton>
          <SaveButton onClick={handleSave} disabled={regexError}>
            保存する
          </SaveButton>
        </ButtonArea>
      </PaperContent>
    </StyledPaper>
  );
};

// --- スタイル定義 ---
const StyledPaperItem = styled(PaperItem)`
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionLabel = styled(Typography)`
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
`;

const DescriptionText = styled(Typography)`
  color: #888;
  font-size: 12px;
  margin-top: 4px;
`;

const FormControlLabelMui = styled(FormControlLabel)(`
  .MuiTypography-body1 {
    color: ${COLOR_TEXT_MAIN};
  }
  .Mui-checked {
    color: ${COLOR_TEXT_MAIN};
  }
  span {
    font-weight: 700;
  }
`);

const StyledInputField = withStyles({
  root: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    marginRight: '12px',
    border: '1px solid #e6e7e8',
    borderRadius: '4px',
    background: '#fafbfc',
  },
})(TextField);

const ButtonArea = styled(StyledPaperItem)`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
`;

const CancelButton = styled(PaperButton)`
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
`;

const SaveButton = styled(PaperButton)``;

const GroupList = styled('div')`
  padding: 0;
  margin: 16px 0 0 0;
  width: 100%;
`;

const GroupCard = styled('div')`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 16px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const GroupCardHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
`;

const GroupHeaderLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const GroupHeaderRight = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GroupNameInput = styled('input')<{ hasError?: boolean }>`
  flex: 1;
  padding: 6px 8px;
  font-size: 15px;
  font-weight: 500;
  border: ${(props) => (props.hasError ? '2px solid #f44336' : 'none')};
  background: ${(props) => (props.hasError ? '#ffebee' : 'transparent')};
  outline: none;
  border-radius: 4px;
  &:focus {
    background: white;
    border: ${(props) => (props.hasError ? '2px solid #f44336' : '1px solid #5c6bc0')};
    padding: 6px 8px;
  }
`;

const BlacklistLabel = styled('div')`
  flex: 1;
  padding: 6px 8px;
  font-size: 15px;
  font-weight: 500;
  color: #333;
`;

const PatternCountChip = styled(Chip)`
  background-color: #e8eaf6;
  color: #5c6bc0;
  font-size: 12px;
  height: 24px;
  font-weight: 500;
`;

const PathListContainer = styled('div')`
  padding: 16px 24px 16px 56px;
`;

const PathItemRow = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
`;

const PathNumber = styled('div')`
  color: #999;
  font-size: 14px;
  min-width: 24px;
`;

const PathInputField = styled('input')`
  flex: 1;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  &:focus {
    outline: none;
    border-color: #5c6bc0;
  }
`;

const AddPatternLink = styled('button')`
  background: none;
  border: none;
  color: #5c6bc0;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: #3f51b5;
  }
`;

const AddGroupButton = styled('button')`
  width: 100%;
  padding: 16px;
  background: white;
  border: 2px dashed #d0d0d0;
  border-radius: 8px;
  color: #666;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    border-color: #5c6bc0;
    color: #5c6bc0;
    background: #f8f9fa;
  }
`;

const InputError = styled(Typography)`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;

export default LinkPrefetchEditTab;
